import React from 'react';
import { Link } from '../Link';
import { Tooltip } from '../Tooltip';
import type { IntlShape } from 'react-intl';

export interface LogoProps {
  intl?: IntlShape;
  /** Pre-generated tooltip when rendering without IntlProvider (e.g. static SRP). */
  tooltipLabel?: string;
  clearSearch?: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  variant?: 'horizontal' | 'compact' | 'icon';
  className?: string;
  /** Pass when rendering outside Vike (e.g. SRP island) so Link can build correct hrefs */
  locale?: string;
}

const DEFAULT_LOGO_TOOLTIP = 'Go to homepage and clear search';

/**
 * Logo component for navigation
 * horizontal: full logo + text, xl+ only
 * compact: logo + text, below xl
 * icon: icon-only small logo for scroll row / small screens
 */
export const Logo: React.FC<LogoProps> = ({
  intl,
  tooltipLabel,
  clearSearch,
  variant = 'horizontal',
  className = '',
  locale,
}) => {
  const tooltipContent =
    tooltipLabel ??
    (intl
      ? intl.formatMessage({
          id: 'tooltip.logo',
          defaultMessage: DEFAULT_LOGO_TOOLTIP,
          description: 'Tooltip text for logo/home button that navigates to homepage and clears search',
        })
      : DEFAULT_LOGO_TOOLTIP);

  if (variant === 'icon') {
    return (
      <Tooltip content={tooltipContent} position="bottom">
        <Link
          href="/"
          onClick={clearSearch}
          locale={locale}
          className={`inline-flex items-center justify-center flex-shrink-0 cursor-pointer rounded-lg lg:hidden ${className}`}
        >
          <img
            src="/logo.svg"
            height="32"
            width="32"
            alt="Logo"
            loading="lazy"
            className="object-contain h-8 w-8"
          />
        </Link>
      </Tooltip>
    );
  }

  if (variant === 'compact') {
    return (
      <Tooltip content={tooltipContent} position="bottom">
        <Link
          href="/"
          onClick={clearSearch}
          locale={locale}
          className={`inline-flex items-center xl:hidden gap-2 cursor-pointer h-9 w-auto rounded-xl ${className}`}
        >
          <img
            src="/logo.svg"
            height="36"
            width="36"
            alt="Logo"
            loading="lazy"
            className="object-contain h-9 w-9"
          />
          <span className="text-lg font-bold text-gray-900 dark:text-white">JobCrawls</span>
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={tooltipContent} position="bottom">
      <Link
        href="/"
        onClick={clearSearch}
        locale={locale}
        className={`hidden xl:inline-flex xl:items-center gap-2 cursor-pointer h-10 w-auto rounded-xl ${className}`}
      >
        <img
          src="/logo.svg"
          height="40"
          width="40"
          alt="Logo"
          loading="lazy"
          className="object-contain h-10 w-10"
        />
        <span className="text-xl font-bold text-gray-900 dark:text-white">JobCrawls</span>
      </Link>
    </Tooltip>
  );
};
